import { __assign, __rest } from "tslib";
import React, { forwardRef } from 'react';
import MuiButton from '@mui/material/Button';
import withStyles from '@mui/styles/withStyles';
import clsx from 'clsx';
import Loader from '../Loader/Loader';
var styles = function (theme) { return ({
    containedError: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:focus': {
            backgroundColor: theme.palette.error.dark,
        },
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
    textError: {
        color: theme.palette.error.main,
        '&::after': {
            backgroundColor: theme.palette.error.main,
        },
    },
    // loading styles
    loading: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    root: {},
    label: {},
    text: {
        display: 'inline-flex',
        alignItems: 'center',
        '&:hover': {
            // coloring is achieved with an ::after instead
            background: 'transparent',
        },
    },
    textPrimary: {
        '&:hover': {
            // coloring is achieved with an ::after instead
            background: 'transparent',
        },
    },
    textSecondary: {
        '&:hover': {
            // coloring is achieved with an ::after instead
            background: 'transparent',
        },
    },
    // applied to a text variant button itself if it contains an icon
    textIcon: {
        textTransform: 'uppercase',
    },
    // style overrides to correct the shape of the touch ripple for
    // text buttons
    textTouchRipple: {
        left: '-0.3em',
        width: 'calc(100% + 0.6em)',
        height: 'calc(100% + 0.2em)',
        bottom: '0',
        borderRadius: '2em',
        top: '-0.125em',
    },
    outlined: {},
    outlinedPrimary: {},
    outlinedSecondary: {},
    // applied to an outlined variant button itself if it contains an icon
    outlinedIcon: {},
    contained: {},
    containedPrimary: {},
    containedSecondary: {},
    // applied to a contained variant button itself if it contains an icon
    containedIcon: {},
    focusVisible: {},
    disabled: {},
    colorInherit: {},
    sizeLarge: {},
    sizeSmall: {},
    fullWidth: {},
    textSizeLarge: {},
    textSizeSmall: {},
    outlinedSizeLarge: {},
    outlinedSizeSmall: {},
    containedSizeLarge: {},
    containedSizeSmall: {},
    disableElevation: {},
    containedInherit: {},
    containedSizeMedium: {},
    outlinedInherit: {},
    outlinedSizeMedium: {},
    sizeMedium: {},
    textInherit: {},
    textSizeMedium: {},
    // the following icon classes are built-in MUI Button classes, which we
    // customize due to sizing constraints
    startIcon: function (props) { return ({
        marginRight: props.variant === 'text' ? theme.spacing(0.5) : theme.spacing(1),
        lineHeight: 1,
    }); },
    endIcon: function (props) { return ({
        marginLeft: props.variant === 'text' ? theme.spacing(0.5) : theme.spacing(1),
        lineHeight: 1,
    }); },
    iconSizeLarge: {
        '& > *:first-child': {
            fontSize: theme.typography.pxToRem(20),
        },
    },
    iconSizeMedium: {
        '& > *:first-child': {
            fontSize: theme.typography.pxToRem(16),
        },
    },
    iconSizeSmall: {
        '& > *:first-child': {
            fontSize: theme.typography.pxToRem(10),
        },
    },
}); };
/**
 * A Button is used for most call-to-action intents in an application. Use
 * the default 'contained' variant for main actions like form submission,
 * or the 'text' variant for secondary actions like toggling a view or
 * opening a modal.
 * @public
 */
var Button = forwardRef(function (_a, ref) {
    var _b = _a.variant, givenVariant = _b === void 0 ? 'contained' : _b, className = _a.className, classes = _a.classes, startIcon = _a.startIcon, endIcon = _a.endIcon, children = _a.children, loading = _a.loading, TouchRippleProps = _a.TouchRippleProps, color = _a.color, disabled = _a.disabled, rest = __rest(_a, ["variant", "className", "classes", "startIcon", "endIcon", "children", "loading", "TouchRippleProps", "color", "disabled"]);
    var hasIcon = startIcon || endIcon;
    // extracting needed class names
    var _c = classes || {
        textIcon: null,
        outlinedIcon: null,
        containedIcon: null,
        textTouchRipple: null,
        containedError: null,
        textError: null,
        loading: null,
    }, textIcon = _c.textIcon, outlinedIcon = _c.outlinedIcon, containedIcon = _c.containedIcon, textTouchRipple = _c.textTouchRipple, containedError = _c.containedError, textError = _c.textError, loadingClass = _c.loading, restClasses = __rest(_c, ["textIcon", "outlinedIcon", "containedIcon", "textTouchRipple", "containedError", "textError", "loading"]);
    var _d = TouchRippleProps || { className: null }, touchRippleClassName = _d.className, restTouchRippleProps = __rest(_d, ["className"]);
    // swap out the variant if the user requested a contained
    // secondary button. Secondary is the only color that uses
    // MUI's 'outlined' variant, so we just kinda hack it in here
    // and hide it externally
    var variant = color === 'secondary' && givenVariant === 'contained'
        ? 'outlined'
        : givenVariant;
    return (React.createElement(MuiButton, __assign({ variant: variant }, rest, { disabled: disabled || loading, classes: restClasses, className: clsx(variant === 'outlined' && !!hasIcon && outlinedIcon, variant === 'text' && !!hasIcon && textIcon, variant === 'contained' && !!hasIcon && containedIcon, 
        // patching in error color variants
        variant === 'text' && color === 'error' && textError, variant === 'contained' && color === 'error' && containedError, loading && loadingClass, className), 
        /* can't think of any more elegant way to make error color work here */
        color: color !== 'error' ? color : 'inherit', TouchRippleProps: __assign(__assign({}, restTouchRippleProps), { className: clsx(variant === 'text' && textTouchRipple, touchRippleClassName) }), startIcon: startIcon, endIcon: endIcon, ref: ref }), loading ? React.createElement(Loader, { size: "1.5em" }) : children));
});
// @ts-expect-error https://material-ui.com/guides/composition/#wrapping-components
Button.muiName = MuiButton.muiName;
export default withStyles(styles, { name: 'BwButton' })(Button);
/** @private docs only! */
export var DocsButton = function (props) { return React.createElement(Button, __assign({}, props)); };
//# sourceMappingURL=Button.js.map